import {
  Box,
  Center,
  CloseButton,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
} from '@mantine/core'
import { useEffect, useMemo, useState } from 'react'

import {
  BOGGLE_RUN_SUMMARY_RATINGS,
  type BoggleRunSummaryRatingId,
} from '~/boggle/config/runSummaryScore'
import type { BoggleCompletedRun } from '~/boggle/progress/types'
import { BogglePlayedDateTime } from '~/boggle/ui/atoms/BogglePlayedDateTime'
import { IconGlyph } from '~/boggle/ui/atoms/Icons'
import { RatingBadge } from '~/boggle/ui/ratings/RatingBadge'
import { RatingFilter, type RatingFilterValue } from '~/boggle/ui/ratings/RatingFilter'
import { ChartBarIcon } from '@phosphor-icons/react'

import { RunDetails } from './RunDetails'
import { formatBogglePlayedDateTime } from '~/boggle/ui/formatBogglePlayedDateTime'

import {
  formatBoggleRunHistoryFiniteNumber,
  sortBoggleCompletedRunsNewestFirst,
} from './runHistoryUtils'

const BASE_RUN_RATING_FILTERS: readonly BoggleRunSummaryRatingId[] = [
  'bronze',
  'silver',
  'gold',
]

function buildRunHistoryRatingFilterValues(
  runs: readonly BoggleCompletedRun[],
): RatingFilterValue[] {
  const tiersPresent = new Set(runs.map(run => run.ratingId))
  const extraTierIds = BOGGLE_RUN_SUMMARY_RATINGS.map(rating => rating.id).filter(
    id =>
      id !== 'none' &&
      !(BASE_RUN_RATING_FILTERS as readonly string[]).includes(id) &&
      tiersPresent.has(id),
  )
  return ['all', ...BASE_RUN_RATING_FILTERS, ...extraTierIds]
}

function hasValidBoggleRunHistoryScore(run: BoggleCompletedRun) {
  const score = run.summarySnapshot?.total
  return typeof score === 'number' && Number.isFinite(score)
}

/** First visible history surface; modal hosts provide repository state. */
export function RunHistory({
  failed,
  initialSelectedRunId = null,
  loading,
  onClose,
  runs,
}: {
  failed?: boolean
  initialSelectedRunId?: string | null
  loading: boolean
  onClose?: () => void
  runs: readonly BoggleCompletedRun[]
}) {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialSelectedRunId)
  const [ratingFilter, setRatingFilter] = useState<RatingFilterValue>('all')
  const scoredRuns = useMemo(
    () => sortBoggleCompletedRunsNewestFirst(runs).filter(hasValidBoggleRunHistoryScore),
    [runs],
  )
  const ratingFilterValues = useMemo(
    () => buildRunHistoryRatingFilterValues(scoredRuns),
    [scoredRuns],
  )

  useEffect(() => {
    if (!ratingFilterValues.includes(ratingFilter)) {
      setRatingFilter('all')
    }
  }, [ratingFilter, ratingFilterValues])

  const orderedRuns = useMemo(() => {
    if (ratingFilter === 'all') {
      return scoredRuns
    }
    return scoredRuns.filter(run => run.ratingId === ratingFilter)
  }, [ratingFilter, scoredRuns])
  const selectedRun =
    selectedRunId === null ? null : orderedRuns.find(run => run.id === selectedRunId)

  if (selectedRun) {
    const selectedIndex = orderedRuns.findIndex(run => run.id === selectedRun.id)
    return (
      <RunDetails
        hasNext={selectedIndex >= 0 && selectedIndex < orderedRuns.length - 1}
        hasPrevious={selectedIndex > 0}
        onBack={() => setSelectedRunId(null)}
        onClose={onClose}
        onNavigateNext={() => {
          const nextRun = orderedRuns[selectedIndex + 1]
          if (nextRun) {
            setSelectedRunId(nextRun.id)
          }
        }}
        onNavigatePrevious={() => {
          const previousRun = orderedRuns[selectedIndex - 1]
          if (previousRun) {
            setSelectedRunId(previousRun.id)
          }
        }}
        run={selectedRun}
      />
    )
  }

  return (
    <Stack
      data-testid="boggle-run-history"
      gap={0}
      h="100%"
      mih={0}
      pb={14}
      style={{ overflow: 'hidden' }}
    >
      {onClose ? (
        <Box pos="relative" w="100%">
          <CloseButton
            aria-label="Close run history"
            c="white"
            data-testid="run-history-close"
            iconSize={28}
            onClick={onClose}
            pos="absolute"
            right={0}
            top={0}
            variant="transparent"
          />
        </Box>
      ) : null}

      <Group justify="space-between" pb={14} pr={14} pl={20} w="100%" wrap="nowrap">
        <Group gap={5} c="rgba(232, 224, 210, 0.7)" fz={15} align="center">
          <Group gap={2} align="center">
            <Text c="white" data-testid="run-history-run-count" fz={15} lh={1}>
              {orderedRuns.length}
            </Text>
            {ratingFilter !== 'all' ? (
              <Text fz={15} lh={1} ml={2}>
                of{' '}{scoredRuns.length}
              </Text>
            ) : null}
          </Group>
          <Text fz={15} lh={1}>
            games played
          </Text>
        </Group>
        <Box pos="relative" style={{ flexShrink: 0 }} w={140}>
          <RatingFilter
            ariaLabelPrefix="Filter games by"
            onChange={setRatingFilter}
            testIdPrefix="run-history-filter-rating"
            value={ratingFilter}
            values={ratingFilterValues}
            variant="select"
          />
        </Box>
      </Group>

      {loading ? (
        <Center data-testid="run-history-loading" flex={1} mih={0}>
          <Loader color="gray" type="dots" />
        </Center>
      ) : failed ? (
        <Center data-testid="run-history-error" flex={1} mih={0}>
          <Text c="rgba(232, 224, 210, 0.7)" fz={15}>
            Unable to load run history.
          </Text>
        </Center>
      ) : scoredRuns.length === 0 ? (
        <Center data-testid="run-history-empty" flex={1} mih={0}>
          <Text c="rgba(232, 224, 210, 0.7)" fz={15}>
            No completed games yet
          </Text>
        </Center>
      ) : orderedRuns.length === 0 ? (
        <Center data-testid="run-history-filter-empty" flex={1} mih={0}>
          <Text c="rgba(232, 224, 210, 0.7)" fz={15}>
            No matching games.
          </Text>
        </Center>
      ) : (
        <ScrollArea flex={1} mih={0} type="auto">
          <Stack gap={8} px={14} data-testid="run-history-list">
            {orderedRuns.map(run => (
              <RunHistoryRow
                key={run.id}
                onSummary={() => setSelectedRunId(run.id)}
                run={run}
              />
            ))}
          </Stack>
        </ScrollArea>
      )}
    </Stack>
  )
}

function RunHistoryRow({
  onSummary,
  run,
}: {
  onSummary: () => void
  run: BoggleCompletedRun
}) {
  const score = formatBoggleRunHistoryFiniteNumber(run.summarySnapshot?.total)
  const playedAt = formatBogglePlayedDateTime(run.completedAt, {
    date: 'Run date unavailable',
    time: 'Run time unavailable',
  })

  return (
    <Group
      align="center"
      aria-label={`View run details for ${playedAt.date} ${playedAt.time}`}
      bd="1px solid rgba(255, 255, 255, 0.12)"
      bdrs={12}
      bg="rgba(255, 255, 255, 0.04)"
      data-testid={`run-history-row-${run.id}`}
      gap={14}
      justify="space-between"
      onClick={onSummary}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSummary()
        }
      }}
      p={6}
      role="button"
      style={{ cursor: 'pointer' }}
      tabIndex={0}
      wrap="nowrap"
    >
      <Group gap={4} miw={108} align="center">
        <RatingBadge
          h={40}
          ratingId={run.ratingId}
          testId={`run-history-rating-${run.id}`}
          w={50}
        />
        <Text
          c="white"
          data-testid={`run-history-score-${run.id}`}
          ff="Tilt Neon, sans-serif"
          fw={500}
          fz={24}
          lh={1}
        >
          {score}
        </Text>
      </Group>

      <Group>
        <BogglePlayedDateTime
          align="right"
          isoString={run.completedAt}
          testIdPrefix={`run-history-played-${run.id}`}
        />
        <Box aria-hidden c="rgba(255,255,255,0.55)" style={{ display: 'flex' }}>
          <IconGlyph icon={ChartBarIcon} size={22} weight="duotone" />
        </Box>
      </Group>
    </Group>
  )
}
